import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PiSpinnerGapLight,
  PiCheckCircleLight,
  PiWarningCircleLight,
  PiLightbulbLight,
  PiClockLight,
  PiXLight
} from 'react-icons/pi';
import clsx from 'clsx';

export interface ProcessorStep {
  step: string;
  status: 'pending' | 'in_progress' | 'completed' | 'failed';
  message?: string;
  progress_percent?: number;
}

interface InsightProcessorProps {
  isOpen: boolean;
  insightName: string;
  insightTypeId: string;
  steps: ProcessorStep[];
  overallProgress: number;
  elapsedTime: number;
  isComplete: boolean;
  isFailed: boolean;
  errorMessage?: string;
  onClose: () => void;
}

const InsightProcessor: React.FC<InsightProcessorProps> = ({
  isOpen,
  insightName,
  insightTypeId,
  steps,
  overallProgress,
  elapsedTime,
  isComplete,
  isFailed,
  errorMessage,
  onClose
}) => {
  const { t } = useTranslation();
  const [displayTime, setDisplayTime] = useState('0s');

  useEffect(() => {
    if (elapsedTime < 60) {
      setDisplayTime(`${elapsedTime}s`);
    } else {
      const mins = Math.floor(elapsedTime / 60);
      const secs = elapsedTime % 60;
      setDisplayTime(`${mins}m ${secs}s`);
    }
  }, [elapsedTime]);

  const getStepIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <PiCheckCircleLight className="h-5 w-5 text-green-500" />;
      case 'in_progress':
        return <PiSpinnerGapLight className="h-5 w-5 text-blue-500 animate-spin" />;
      case 'failed':
        return <PiWarningCircleLight className="h-5 w-5 text-red-500" />;
      default:
        return <div className="h-5 w-5 rounded-full border-2 border-gray-300 dark:border-gray-600" />;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 dark:bg-black/60 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow-2xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div
          className={clsx(
            'sticky top-0 px-6 py-4 border-b',
            isFailed
              ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-700'
              : isComplete
              ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-700'
              : 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-700'
          )}
        >
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div
                className={clsx(
                  'p-2 rounded-lg',
                  isFailed
                    ? 'bg-red-100 dark:bg-red-900/40'
                    : isComplete
                    ? 'bg-green-100 dark:bg-green-900/40'
                    : 'bg-blue-100 dark:bg-blue-900/40'
                )}
              >
                {isFailed ? (
                  <PiWarningCircleLight
                    className={clsx(
                      'h-6 w-6',
                      isFailed
                        ? 'text-red-600 dark:text-red-400'
                        : isComplete
                        ? 'text-green-600 dark:text-green-400'
                        : 'text-blue-600 dark:text-blue-400'
                    )}
                  />
                ) : isComplete ? (
                  <PiCheckCircleLight className="h-6 w-6 text-green-600 dark:text-green-400" />
                ) : (
                  <PiSpinnerGapLight className="h-6 w-6 text-blue-600 dark:text-blue-400 animate-spin" />
                )}
              </div>

              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white truncate">
                  {isFailed ? t('Generation Failed') : isComplete ? t('Generation Complete') : t('Generating Insight')}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 truncate">
                  {insightName}
                </p>
              </div>
            </div>

            {isComplete || isFailed ? (
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors flex-shrink-0"
                aria-label={t('Close')}
              >
                <PiXLight className="h-5 w-5 text-gray-600 dark:text-gray-400" />
              </button>
            ) : null}
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Progress Bar */}
          {!isFailed && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('Overall Progress')}
                </span>
                <span className="text-sm font-semibold text-gray-900 dark:text-white">
                  {Math.round(overallProgress)}%
                </span>
              </div>
              <div className="w-full h-3 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div
                  className={clsx(
                    'h-full transition-all duration-300 bg-gradient-to-r',
                    isComplete
                      ? 'from-green-400 to-green-600'
                      : 'from-blue-400 to-blue-600'
                  )}
                  style={{ width: `${overallProgress}%` }}
                />
              </div>
            </div>
          )}

          {/* Elapsed Time */}
          {!isComplete && !isFailed && (
            <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
              <PiClockLight className="h-4 w-4" />
              <span>{t('Elapsed time')}: {displayTime}</span>
            </div>
          )}

          {/* Steps */}
          <div className="space-y-3">
            <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
              {t('Processing Steps')}
            </h4>

            {steps.length === 0 ? (
              <div className="text-center py-6">
                <PiSpinnerGapLight className="h-8 w-8 text-blue-500 animate-spin mx-auto mb-3" />
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t('Initializing...')}
                </p>
              </div>
            ) : (
              steps.map((step, idx) => (
                <div
                  key={idx}
                  className={clsx(
                    'p-3 rounded-lg border transition-colors',
                    step.status === 'completed'
                      ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-700'
                      : step.status === 'failed'
                      ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-700'
                      : step.status === 'in_progress'
                      ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-700'
                      : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700'
                  )}
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 pt-0.5">
                      {getStepIcon(step.status)}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <h5 className="text-sm font-medium text-gray-900 dark:text-white">
                          {step.step}
                        </h5>
                        {step.progress_percent !== undefined && step.status === 'in_progress' && (
                          <span className="text-xs font-semibold text-blue-600 dark:text-blue-400">
                            {Math.round(step.progress_percent)}%
                          </span>
                        )}
                      </div>

                      {step.message && (
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 break-words">
                          {step.message}
                        </p>
                      )}

                      {step.progress_percent !== undefined && step.status === 'in_progress' && (
                        <div className="mt-2 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-blue-500 transition-all duration-300"
                            style={{ width: `${step.progress_percent}%` }}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Error Message */}
          {isFailed && errorMessage && (
            <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg">
              <div className="flex gap-3">
                <PiWarningCircleLight className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <h5 className="text-sm font-semibold text-red-900 dark:text-red-200 mb-1">
                    {t('Error Details')}
                  </h5>
                  <p className="text-sm text-red-800 dark:text-red-300 break-words">
                    {errorMessage}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Success Message */}
          {isComplete && !isFailed && (
            <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg">
              <div className="flex gap-3">
                <PiCheckCircleLight className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                <div>
                  <h5 className="text-sm font-semibold text-green-900 dark:text-green-200">
                    {t('Insight generated successfully')}
                  </h5>
                  <p className="text-sm text-green-800 dark:text-green-300 mt-1">
                    {t('Your insight is ready and has been saved to the insights list.')}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {(isComplete || isFailed) && (
          <div className="sticky bottom-0 px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
            <button
              onClick={onClose}
              className="w-full px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg font-medium transition-colors"
            >
              {t('Close')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default InsightProcessor;
